<template>
  <!-- This file will serve as the hub in which the user will go over a few steps to ensure all relevant transactions (for selected month) are loaded into the monthly overview. -->
  <BModal
    v-model="modal"
    ref="modalRef"
    title="End of Month Budget Template Transaction"
    ok-title="Save"
    ok-variant="success"
    @show="prefillFieldsOnInit"
    @ok="handleSaveTrsxn"
  >
    <div class="row">
      <div class="col-12">
        <div
          class="d-flex flex-wrap justify-content-between align-items-center"
        ></div>
      </div>
      <div class="col-12">
        <h6>Transaction Details</h6>
        <BFormGroup
          id="input-group-institution"
          label="Institution:"
          label-for="institution-options"
          class="floating-group"
        >
          <BFormSelect
            id="institution-options"
            v-model="institution"
            :class="{ 'is-invalid': instError !== null }"
            :options="institutionOpts"
            style="font-size: 0.85rem"
          />
        </BFormGroup>
        <BFormGroup
          id="input-group-amount"
          label="Amount:"
          label-for="amount-input"
          class="floating-group"
        >
          <BFormInput
            id="amount-input"
            v-model="amount"
            type="number"
            step="0.01"
            :class="{ 'is-invalid': amountError !== null }"
            placeholder="0.00"
            style="font-size: 0.78rem"
          />
        </BFormGroup>
        <BFormGroup
          id="input-group-date"
          label="Date:"
          label-for="date"
          class="floating-group"
        >
          <BFormInput
            id="date"
            v-model="trsxnDate"
            type="date"
            size="sm"
            style="font-size: 0.85rem"
            :class="{ 'is-invalid': trsxnDateError !== null }"
          />
        </BFormGroup>
        <BFormGroup
          id="input-group-description"
          label="Description:"
          label-for="description-input"
          class="floating-group"
        >
          <BFormTextarea
            id="description-input"
            v-model="description"
            :class="{ 'is-invalid': descriptionError !== null }"
            placeholder="Enter something..."
            rows="3"
            style="font-size: 0.85rem"
          />
        </BFormGroup>
      </div>
      <div class="col-12">
        <h6>Categories</h6>
        <BFormGroup
          id="input-group-template-filter"
          label="Template:"
          label-for="template-filter"
          class="floating-group"
        >
          <BFormSelect
            id="template-filter"
            :modelValue="selectedTemplate"
            :options="templatesOpts"
            size="sm"
            style="font-size: 0.85rem"
            @update:modelValue="handleTemplateSelection($event)"
          />
        </BFormGroup>
        <template v-if="categories">
          <div class="row">
            <div class="col-6">
              <BFormGroup
                :id="`input-group-cats-without-pending`"
                :label="`Categories Sum Excluding Pending:`"
                :label-for="`cats-without-pending`"
                class="floating-group px-3"
              >
                <BFormInput
                  v-model.number="catsTotalWithoutPending"
                  type="number"
                  step="0.01"
                  size="sm"
                  placeholder="0.00"
                  readonly
                  disabled
                  style="font-size: 0.78rem"
                />
              </BFormGroup>
            </div>
            <div class="col-6">
              <BFormGroup
                :id="`input-group-cats-with-pending`"
                :label="`Categories Sum Including Pending:`"
                :label-for="`cats-with-pending`"
                class="floating-group px-3"
              >
                <BFormInput
                  v-model.number="catsTotalIncludingPending"
                  type="number"
                  step="0.01"
                  size="sm"
                  placeholder="0.00"
                  readonly
                  disabled
                  style="font-size: 0.78rem"
                />
              </BFormGroup>
            </div>
            <div
              class="col-6"
              v-for="(cat, catUid) in categories"
              :key="catUid"
            >
              <BFormGroup
                :id="`input-group-spending-category-${catUid}`"
                :label="`${cat.name} Amount:`"
                :label-for="`spending-category-${catUid}`"
                class="floating-group px-3"
              >
                <BFormInput
                  v-model.number="cat.amount"
                  type="number"
                  step="0.01"
                  size="sm"
                  placeholder="0.00"
                  style="font-size: 0.78rem"
                />
              </BFormGroup>
            </div>
          </div>
        </template>
      </div>
    </div>
  </BModal>
</template>
<script>
import {
  BModal,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BFormTextarea,
} from "bootstrap-vue-next";
import {
  bankingInstitutions as bankingInstitutionsNs,
  userSession as userSessionNs,
} from "@/store/namespaces";
import debounce from "lodash.debounce";

export default {
  name: "EndOfMonthTrsxnModal",
  components: {
    BModal,
    BFormGroup,
    BFormSelect,
    BFormInput,
    BFormTextarea,
  },
  props: {
    acctUid: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      savingTrsxn: false,
      modal: false,
      institution: null,
      amount: null,
      description: null,
      instError: null,
      amountError: null,
      descriptionError: null,
      trsxnDate: null,
      trsxnDateError: null,
      selectedTemplate: null,
      fetchedTemplates: null,
      categories: null,
    };
  },
  computed: {
    catsTotalWithoutPending() {
      if (!this.categories) return 0;

      const copy = { ...this.categories };
      if (copy.pending) delete copy.pending;

      return Object.values(copy).reduce((acc, category) => {
        return acc + (category.amount || 0);
      }, 0);
    },
    catsTotalIncludingPending() {
      if (!this.categories) return 0;

      return Object.values(this.categories).reduce((acc, category) => {
        return acc + (category.amount || 0);
      }, 0);
    },
    templatesOpts() {
      if (!this.fetchedTemplates) {
        return [{ value: null, text: "— Select —" }];
      }

      const opts = [{ value: null, text: "— Select —" }];

      this.fetchedTemplates.forEach((template) => {
        opts.push({
          value: template.item,
          text: template.item.name,
        });
      });

      return opts;
    },
    bankingInstitutions() {
      return this.$store.getters[
        `${bankingInstitutionsNs}/getBankingInstitutions`
      ];
    },
    institutionOpts() {
      const opts = [{ value: null, text: "— Select —" }];

      if (!this.bankingInstitutions) return opts;

      const institutionOpts = this.bankingInstitutions.map((inst) => {
        const uidLast6 = inst.uid.slice(-6);
        return {
          value: inst,
          text: `${inst.name} - ${inst.acct_last_4} (${uidLast6})`,
        };
      });

      return opts.concat(institutionOpts);
    },
  },
  watch: {
    // Make sure to refetch transactions when the selected account changes
    acctUid(newAcctUid, oldAcctUid) {
      if (newAcctUid !== oldAcctUid) {
        this.fetchTemplates();
      }
    },
  },
  mounted() {
    this.fetchTemplates();
  },
  methods: {
    prefillFieldsOnInit() {
      if (this.trsxnDate === null) {
        const today = new Date();
        this.trsxnDate = new Date(today.getFullYear(), today.getMonth() + 1, 0)
          .toISOString()
          .substring(0, 10); // Set default date to last day of current month
      }

      if (this.institution === null && this.institutionOpts.length > 1) {
        // Attempt to find the institution whose name is 'lendingClub' (case-insensitive) and preselect it if it exists
        const lendingClubInst = this.institutionOpts.find((inst) =>
          inst.text.toLowerCase().includes("lendingclub")
        );
        if (lendingClubInst) {
          this.institution = lendingClubInst.value;
        }
      }

      if (this.amount === null) {
        this.amount = 0.0;
      }

      if (this.description === null) {
        this.description = "End of month budget template transaction";
      }
    },
    fetchTemplates: debounce(async function () {
      if (!this.acctUid) {
        console.warn("Cannot fetch templates because acctUid is not defined.");
        return;
      }

      try {
        const response = await this.$api.get("/budget-templates", {
          params: {
            filter: [`acctUid:eq:${this.acctUid}`],
          },
        });

        this.fetchedTemplates = response.data;
      } catch (error) {
        console.error("Error fetching templates:", error);
      }
    }, 300),
    handleTemplateSelection(template) {
      if (!template || !template.categories) {
        this.categories = null;
        return;
      }

      // Transform template categories array into an object where the keys are the category_uid and the values are the category details + budget_amount
      const categoriesObj = {};
      template.categories.forEach((category) => {
        categoriesObj[category.category_uid] = {
          name: category.name,
          amount: category.budget_amount,
        };
      });

      this.categories = categoriesObj;
    },
    async handleSaveTrsxn(bvEvt) {
      bvEvt.preventDefault(); // Prevent the modal from closing immediately
      const isValid = this.validateForm();
      if (!isValid) {
        this.$notify.notifyError(
          "Please correct the errors in the form before submitting."
        );
        return;
      }

      try {
        this.savingTrsxn = true;

        const userUid = await this.getUserUid();
        if (!userUid) return;
        const createdTrsxnUid = await this.createTrsxn(userUid);
        await this.updateTrsxn(createdTrsxnUid, userUid);

        // After successful save:
        this.resetModalState();
        this.$refs.modalRef.hide();
        this.$notify.notifySuccess("Transaction saved successfully.");
        this.$emit("transactionCreated");
      } catch (error) {
        console.error("Error saving transaction:", error);
        this.$notify.notifyError(
          "An error occurred while saving the transaction. Please try again."
        );
      } finally {
        this.savingTrsxn = false;
      }
    },
    async updateTrsxn(trsxnUid, userUid) {
      try {
        const categories = [];
        Object.entries(this.categories).forEach(([catUid, catDetails]) => {
          // Only include categories that have a non-zero amount to avoid cluttering the transaction with zero-amount categories
          if (!catDetails.amount || Math.abs(catDetails.amount) < 0.01) return;

          categories.push({
            spending_category_uid: catUid,
            fixed_amount: catDetails.amount,
          });
        });

        const payload = {
          acct_uid: this.acctUid,
          user_uid: userUid,
          transactions: [
            {
              transaction_uid: trsxnUid,
              snapshot_categories: categories,
            },
          ],
        };

        await this.$api.patch(`/banking-institutions/transactions`, payload);
      } catch (error) {
        console.error("Error updating transaction:", error);
        throw new Error("Failed to update transaction with categories");
      }
    },
    async createTrsxn(userUid) {
      try {
        const payload = {
          acct_uid: this.acctUid,
          user_uid: userUid,
          institution_name: this.institution.name,
          transactions: [
            {
              amount: Number(this.amount).toFixed(2), // Ensure amount is sent as a string with 2 decimal places
              date: this.trsxnDate,
              description: this.description,
              ...(this.categories
                ? {
                    categories: Object.entries(this.categories).map(
                      ([catUid, catDetails]) => ({
                        category_uid: catUid,
                        amount: catDetails.amount,
                      })
                    ),
                  }
                : {}),
            },
          ],
        };

        const response = await this.$api.post(
          `/banking-institutions/${this.institution.uid}/transactions`,
          payload
        );

        return response.data.item.created_transactions[0].transaction_uid; // Return the UID of the created transaction
      } catch (error) {
        console.error("Error creating transaction:", error);
        throw new Error("Failed to create transaction");
      }
    },
    validateForm() {
      let isValid = true;

      if (!this.institution) {
        this.instError = "Please select an institution.";
        isValid = false;
      } else {
        this.instError = null;
      }

      if (this.amount === null || isNaN(this.amount)) {
        this.amountError = "Please enter a valid amount.";
        isValid = false;
      } else {
        this.amountError = null;
      }

      if (!this.description || this.description.trim() === "") {
        this.descriptionError = "Description cannot be empty.";
        isValid = false;
      } else {
        this.descriptionError = null;
      }

      if (!this.trsxnDate) {
        this.trsxnDateError = "Please select a date.";
        isValid = false;
      } else {
        this.trsxnDateError = null;
      }

      // If no categories are selected, also an error
      if (!this.categories || Object.keys(this.categories).length === 0) {
        this.$notify.notifyError("Please select a template with categories.");
        isValid = false;
      }

      // If categories are selected and their sum is not 0, also an error
      if (Math.abs(this.catsTotalWithPending) - 0 > 0.01) {
        this.$notify.notifyError(
          "The sum of the category amounts (including pending) must be 0."
        );
        isValid = false;
      }

      return isValid;
    },
    async getUserUid() {
      const userInfo = await this.$store.dispatch(
        `${userSessionNs}/getUserInfo`
      );
      const userId = userInfo?.id;

      if (!userId) {
        this.$notify.notifyError(
          "User information is missing. Please ensure you are logged in and try again."
        );
        return null;
      }
      return userId;
    },
    resetModalState() {
      this.modal = false;
      this.institution = null;
      this.instError = null;
      this.amount = null;
      this.amountError = null;
      this.description = null;
      this.descriptionError = null;
      this.trsxnDate = null;
      this.trsxnDateError = null;
      this.selectedTemplate = null;
      this.categories = null;
    },
  },
};
</script>
